import { useMemo, useState } from 'react';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { ESQUEMA_DESPACHOS } from '@/lib/filtros-esquemas';
import { resolverRango, type TokenRango } from '@/lib/rango-fechas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCopy, ClipboardList, MapPin, Save } from 'lucide-react';
import * as jobsApi from '@/api/jobs.api';
import * as employeesApi from '@/api/employees.api';
import * as installationApi from '@/api/installation.api';
import type { JobWithRelations } from '@/api/jobs.api';
import type { DespachoSnapshot, FormaPago } from '@conversia/shared-types';
import { renderFichaDespacho, renderFichasDespacho } from '@/lib/ficha-despacho';
import { agruparPorDia, rangoDesdeInputs, toInputValue } from '@/lib/despachos-fechas';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/hooks/useToast';
import { FiltrosGuardados } from '@/components/filtros/FiltrosGuardados';
import { Cargando } from '@/components/common/Cargando';

/**
 * DESPACHOS (hoy y mañana) — el producto del ritual nocturno: por cada trabajo
 * con cita, la ficha de despacho en el formato EXACTO que el equipo copia al
 * grupo de WhatsApp. Los campos que el chat no captura (vendedor, pago, RNC,
 * compañía, link de Maps) se corrigen inline y se guardan al snapshot; lo que
 * falta sale como "—" (nunca se inventa). "Copiar ficha" / "Copiar todas"
 * llevan el texto al portapapeles — el envío automático al grupo es fase 2.
 */

const PAGO_OPTIONS = [
  { value: '', label: 'Pago: —' },
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'MIXTO', label: 'Mixto' },
];

const RNC_OPTIONS = [
  { value: '', label: 'RNC: —' },
  { value: 'si', label: 'RNC: Sí' },
  { value: 'no', label: 'RNC: No' },
];

/** Valores default de los inputs de fecha: hoy y mañana (hora LOCAL). */
function defaultRango(): { desde: string; hasta: string } {
  const hoy = new Date();
  const manana = new Date(hoy);
  manana.setDate(manana.getDate() + 1);
  return { desde: toInputValue(hoy), hasta: toInputValue(manana) };
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function Despachos() {
  const toast = useToast();
  // Rango elegible con calendario; default = hoy y mañana (comportamiento clásico).
  // A1: el rango vive en la URL. El default sigue siendo "hoy y manana", pero
  // ahora es un TOKEN del catalogo comun en vez de dos fechas calculadas: asi
  // un enlace guardado no envejece -- manana sigue significando manana.
  const { filtros, setFiltros } = useFiltrosUrl<{ rango?: TokenRango; desde?: string; hasta?: string }>(
    ESQUEMA_DESPACHOS,
  );
  const resuelto = useMemo(
    () =>
      resolverRango(
        filtros.rango || filtros.desde || filtros.hasta
          ? { rango: filtros.rango, desde: filtros.desde, hasta: filtros.hasta }
          : { rango: 'hoy-manana' },
      ),
    [filtros.rango, filtros.desde, filtros.hasta],
  );
  // Memoizado: si se recreara en cada render, el useMemo que depende de el se
  // recalcularia siempre y la consulta se repetiria sin motivo.
  const rango = useMemo(
    () => ({
      desde: resuelto.desde ?? toInputValue(new Date()),
      hasta: resuelto.hasta ?? toInputValue(new Date()),
    }),
    [resuelto.desde, resuelto.hasta],
  );
  const setRango = (r: { desde: string; hasta: string }) =>
    setFiltros({ rango: undefined, desde: r.desde, hasta: r.hasta });
  const esDefault = useMemo(() => {
    const def = defaultRango();
    return rango.desde === def.desde && rango.hasta === def.hasta;
  }, [rango]);
  const { from, to } = rangoDesdeInputs(rango.desde, rango.hasta);

  const { data: jobs, isLoading } = useQuery({
    queryKey: ['jobs', 'despachos', rango.desde, rango.hasta],
    queryFn: () => jobsApi.listDespachos(from.toISOString(), to.toISOString()),
  });
  const { data: crmView } = useQuery({
    queryKey: ['installation', 'crm-view'],
    queryFn: installationApi.getCrmView,
  });
  const currencyPrefix = crmView?.currencyPrefix || 'RD$';

  // Técnicos activos para el selector inline de la ficha (mismo set que /jobs).
  const { data: tecnicos } = useQuery({
    queryKey: ['employees', 'tecnicos'],
    queryFn: () => employeesApi.listEmployees({ rol: 'TECNICO', activo: true, take: 100 }),
  });
  const tecnicoOptions = [
    { value: '', label: 'Técnico: sin asignar' },
    ...(tecnicos?.items ?? []).map((t) => ({ value: t.id, label: t.nombre })),
  ];

  // Secciones agrupadas por día local (Hoy / Mañana / "Viernes 24 jul").
  const secciones = agruparPorDia(jobs ?? [], new Date());

  async function copiarTodas() {
    const all = jobs ?? [];
    if (all.length === 0) return;
    const ok = await copyText(renderFichasDespacho(all, currencyPrefix));
    if (ok) toast.success(`${all.length} ficha(s) copiadas — pégalas en el grupo`);
    else toast.error('No se pudo copiar al portapapeles');
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Despachos"
        description="La ficha de cada cita del período elegido, lista para copiar al grupo. Lo que falte sale como “—”."
        action={
          <Button onClick={() => void copiarTodas()} disabled={(jobs ?? []).length === 0}>
            <ClipboardCopy className="mr-1 h-4 w-4" /> Copiar todas
          </Button>
        }
      />
      {/* A2 · Filtros guardados. Lee la ruta y el ?query de la URL por su cuenta. */}
      <FiltrosGuardados />

      {/* Selector de fechas: default hoy y mañana; calendario para cualquier día. */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-500 dark:text-slate-400">
          Desde
          <input
            type="date"
            value={rango.desde}
            onChange={(e) => setRango({ ...rango, desde: e.target.value || rango.desde })}
            className="mt-1 block h-10 rounded-md border border-surface-border bg-white px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
        </label>
        <label className="text-xs text-slate-500 dark:text-slate-400">
          Hasta
          <input
            type="date"
            value={rango.hasta}
            min={rango.desde}
            onChange={(e) => setRango({ ...rango, hasta: e.target.value || rango.hasta })}
            className="mt-1 block h-10 rounded-md border border-surface-border bg-white px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
        </label>
        {!esDefault && (
          <Button variant="outline" size="sm" onClick={() => setRango(defaultRango())}>
            Hoy y mañana
          </Button>
        )}
      </div>

      {isLoading ? (
        <Cargando />
      ) : secciones.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="Sin citas en el período elegido"
          description="Los despachos salen de los trabajos con fecha de cita (botón Agendar en la ficha del lead)."
        />
      ) : (
        <>
          {secciones.map((s) => (
            <Seccion
              key={s.key}
              titulo={`${s.titulo} (${s.jobs.length})`}
              jobs={s.jobs}
              currencyPrefix={currencyPrefix}
              tecnicoOptions={tecnicoOptions}
            />
          ))}
        </>
      )}
    </div>
  );
}

interface TecnicoOption {
  value: string;
  label: string;
}

function Seccion({
  titulo,
  jobs,
  currencyPrefix,
  tecnicoOptions,
}: {
  titulo: string;
  jobs: JobWithRelations[];
  currencyPrefix: string;
  tecnicoOptions: TecnicoOption[];
}) {
  if (jobs.length === 0) return null;
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {titulo}
      </h2>
      <div className="grid gap-4 lg:grid-cols-2">
        {jobs.map((j) => (
          <DespachoCard key={j.id} job={j} currencyPrefix={currencyPrefix} tecnicoOptions={tecnicoOptions} />
        ))}
      </div>
    </section>
  );
}

function DespachoCard({
  job,
  currencyPrefix,
  tecnicoOptions,
}: {
  job: JobWithRelations;
  currencyPrefix: string;
  tecnicoOptions: TecnicoOption[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const snap = (job.despachoJson ?? null) as DespachoSnapshot | null;

  const [vendedor, setVendedor] = useState(snap?.vendedor ?? '');
  const [formaPago, setFormaPago] = useState<string>(snap?.formaPago ?? '');
  const [comprobante, setComprobante] = useState<string>(snap?.comprobanteFiscal ?? '');
  const [compania, setCompania] = useState(snap?.compania ?? '');
  const [mapsUrl, setMapsUrl] = useState(snap?.mapsUrl ?? '');
  const [tecnicoId, setTecnicoId] = useState(job.employeeId ?? '');

  const save = useMutation({
    mutationFn: async () => {
      await jobsApi.updateDespacho(job.id, {
        vendedor: vendedor.trim() || null,
        formaPago: (formaPago || null) as FormaPago | null,
        comprobanteFiscal: (comprobante || null) as 'si' | 'no' | null,
        compania: compania.trim() || null,
        mapsUrl: mapsUrl.trim() || null,
      });
      // El técnico se guarda con el MISMO botón, vía el endpoint de asignación.
      if (tecnicoId !== (job.employeeId ?? '')) {
        await jobsApi.assignEmployee(job.id, tecnicoId || null);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Ficha actualizada');
    },
    onError: () => toast.error('No se pudo guardar la ficha'),
  });

  // Preview con las EDICIONES locales (lo que se copiará tras Guardar).
  const preview = renderFichaDespacho(
    {
      ...job,
      despachoJson: {
        ...(snap ?? ({} as DespachoSnapshot)),
        telefono: snap?.telefono ?? null,
        modelo: snap?.modelo ?? null,
        precio: snap?.precio ?? null,
        rnc: snap?.rnc ?? null,
        zona: snap?.zona ?? null,
        capturedAt: snap?.capturedAt ?? '',
        vendedor: vendedor.trim() || null,
        formaPago: (formaPago || null) as FormaPago | null,
        comprobanteFiscal: (comprobante || null) as 'si' | 'no' | null,
        compania: compania.trim() || null,
        mapsUrl: mapsUrl.trim() || null,
      },
    },
    currencyPrefix,
  );
  const faltantes = (preview.match(/—/g) ?? []).length;

  async function copiar() {
    const ok = await copyText(preview);
    if (ok) toast.success('Ficha copiada');
    else toast.error('No se pudo copiar');
  }

  const cliente = job.contact?.name ?? job.contact?.pushName ?? job.contact?.phone ?? 'Cliente';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{cliente}</CardTitle>
          <div className="flex items-center gap-2">
            {faltantes > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                {faltantes} dato(s) faltante(s)
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => void copiar()}>
              <ClipboardCopy className="mr-1 h-3.5 w-3.5" /> Copiar ficha
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-surface-border bg-slate-50 p-3 font-mono text-xs leading-5 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
          {preview}
        </pre>
        <div className="grid gap-2 md:grid-cols-2">
          <Input
            label="Vendedor"
            placeholder="Nombre del vendedor"
            value={vendedor}
            onChange={(e) => setVendedor(e.target.value)}
          />
          <Select
            label="Forma de pago"
            options={PAGO_OPTIONS}
            value={formaPago}
            onChange={(e) => setFormaPago(e.target.value)}
          />
          <Select
            label="Comprobante fiscal (RNC)"
            options={RNC_OPTIONS}
            value={comprobante}
            onChange={(e) => setComprobante(e.target.value)}
          />
          <Input
            label="Compañía"
            placeholder="Osito"
            value={compania}
            onChange={(e) => setCompania(e.target.value)}
          />
          <Select
            label="Técnico asignado"
            options={tecnicoOptions}
            value={tecnicoId}
            onChange={(e) => setTecnicoId(e.target.value)}
          />
          <div className="md:col-span-2">
            <Input
              label="Link de ubicación (Google Maps)"
              placeholder="https://maps.google.com/?q=18.48,-69.93"
              value={mapsUrl}
              onChange={(e) => setMapsUrl(e.target.value)}
            />
            <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
              <MapPin className="h-3 w-3" />
              Con un link de Maps (incluidos los acortados maps.app.goo.gl, que el sistema
              resuelve solo) el trabajo se pinta en el mapa de Tracking.
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
            <Save className="mr-1 h-3.5 w-3.5" /> Guardar ficha
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
